import React from 'react';
import { WizardFooter, useWizardContext } from '@patternfly/react-core';
import { apiCheckAuthorization } from '../helpers/apiHelpers';
import { useStableAccessToken } from '../hooks/useStableAccessToken';

const accessDeniedMessage = 
  'Not authorized to deploy. Please request access via https://euewizard.docs.teladoc.dev/#/pages/dev?id=access-to-controller';

interface Step1FooterProps {
  isNextDisabled: boolean;
  authError: string | null;
  setAuthError: (err: string | null) => void;
  hideBack?: boolean;
  /** Shown above the footer buttons while isNextDisabled is true, so a disabled Next always has a visible cause. */
  disabledReason?: string | null;
}

export const Step1Footer: React.FC<Step1FooterProps> = ({
  isNextDisabled,
  authError,
  setAuthError,
  hideBack = false,
  disabledReason,
}) => {
  const tokenGetter = useStableAccessToken();
  const {
    activeStep,
    goToNextStep,
    goToPrevStep,
    close,
  } = useWizardContext();

  const handleNext = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setAuthError(null);
    const authorized = await apiCheckAuthorization(tokenGetter);
    if (!authorized) {
      setAuthError(accessDeniedMessage);
      return;
    }
    goToNextStep();
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    close();
  };

  return (
    <>
      {isNextDisabled && disabledReason && (
        <div style={{ color: 'var(--text-secondary, #6a6e73)', fontSize: '13px', padding: '0 24px 8px' }}>
          {disabledReason}
        </div>
      )}
      <WizardFooter
        activeStep={activeStep}
        nextButtonText="Next"
        backButtonText="Back"
        cancelButtonText="Cancel"
        isNextDisabled={isNextDisabled}
        onNext={handleNext}
        onBack={goToPrevStep}
        onClose={handleClose}
        isBackHidden={hideBack}
      />
    </>
  );
};

interface Step3FooterProps {
  onDeploy: () => Promise<void>;
  disableDeploy: boolean;
  isDeploying: boolean;
}

export const Step3Footer: React.FC<Step3FooterProps> = ({
  onDeploy,
  disableDeploy,
  isDeploying
}) => {
  const { activeStep, goToNextStep, goToPrevStep, close } = useWizardContext();
  
  const handleDeploy = async () => {
    try {
      await onDeploy();
    } catch {
      return;
    }
    goToNextStep();
  };
  
  return (
    <WizardFooter
      activeStep={activeStep}
      nextButtonText={isDeploying ? "Deploying…" : "Deploy"}
      backButtonText="Back"
      isNextDisabled={disableDeploy || isDeploying}
      onNext={handleDeploy}
      onBack={goToPrevStep}
      onClose={close}
    />
  );
};

export const StepResultsFooter: React.FC = () => {
  const { activeStep, goToPrevStep, close } = useWizardContext();
  return (
    <WizardFooter
      activeStep={activeStep}
      backButtonText="Back"
      nextButtonText="Close"
      cancelButtonText="Close"
      onBack={goToPrevStep}
      onNext={close}
      onClose={close}
    />
  );
};
